import {
    correlationIdHeader,
    genCorrelationId,
    UnauthenticatedError,
} from "@sp-crm/core";
import { GraphQLFormattedError } from "graphql";
import { extraRequestHeaders } from "util/browser";
import { setErrorTrackingContext } from "util/error-tracking";
import { ClientHttpError } from "util/http";

export class ClientGraphqlError extends Error {
    public readonly skipReporting: boolean;
    constructor(
        public readonly correlationId: string,
        public readonly graphqlErrors: readonly GraphQLFormattedError[],
    ) {
        super(ClientGraphqlError.getFirstErrorMessage(graphqlErrors));

        this.hasErrorType = (errorType: string) => {
            return this.graphqlErrors.some(
                e => e.extensions && e.extensions["errorType"] === errorType,
            );
        };

        this.skipReporting = this.graphqlErrors.some(
            e =>
                e.extensions &&
                "skipReporting" in e.extensions &&
                e.extensions["skipReporting"],
        );
        Object.setPrototypeOf(this, new.target.prototype);
    }

    public hasErrorType = (errorType: string): boolean => {
        return this.graphqlErrors.some(
            e => e.extensions && e.extensions["errorType"] === errorType,
        );
    };

    public hasMessageContaining = (needle: string): boolean => {
        return this.graphqlErrors.some(e => e.message.includes(needle));
    };

    static getFirstErrorMessage(graphqlErrors: readonly GraphQLFormattedError[]) {
        if (graphqlErrors && graphqlErrors.length > 0) {
            return graphqlErrors[0]?.message;
        }

        return "An unknown error occurred.";
    }
}

export class GatewayTimeoutError extends Error {
    constructor(public readonly correlationId: string) {
        super(
            "This search did not complete because it took longer than expected. Please reach out to support if this continues.",
        );
        this.name = "GatewayTimeoutError";
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export class ClientGraphqlNetworkError extends Error {
    constructor(public readonly originalError: Error) {
        super(`Network error: ${originalError?.message || "Fetch error"}`);
        this.name = "ClientGraphqlNetworkError";
    }
}

type HttpResponseBody =
    | {
          type: "json";
          body: Record<string, unknown>;
      }
    | {
          type: "text";
          body: string;
      };

const parseResponseBody = async (res: Response): Promise<HttpResponseBody> => {
    const text = await res.text();
    try {
        return { type: "json", body: JSON.parse(text) };
    } catch (e) {
        return { type: "text", body: text };
    }
};

const makeErrorsArray = (json: Record<string, unknown>) => {
    if ("errors" in json && Array.isArray(json.errors)) {
        return json.errors;
    }
    return [
        {
            message: "An unknown error occurred.",
            locations: [],
        },
    ];
};

const throwGraphqlError = async (res: Response): Promise<never> => {
    if (res.status === 401) {
        throw new UnauthenticatedError();
    }

    if (res.status === 504) {
        throw new GatewayTimeoutError(res.headers.get(correlationIdHeader) || "");
    }

    const responseBody = await parseResponseBody(res);

    if (responseBody.type === "json") {
        const errors = makeErrorsArray(responseBody.body);

        throw new ClientGraphqlError(res.headers.get(correlationIdHeader) || "", errors);
    } else {
        throw new ClientHttpError(
            res.headers.get(correlationIdHeader) || "",
            res.status,
            res.statusText,
            responseBody.body,
        );
    }
};

export function graphqlFetcher<TData, TVariables>(query: string, variables?: TVariables) {
    return async (): Promise<TData> => {
        let res: Response | null = null;

        const correlationId = genCorrelationId("client");

        try {
            res = await fetch("/api/graphql" as string, {
                method: "POST",
                ...{
                    headers: {
                        ...extraRequestHeaders(),
                        "content-type": "application/json",
                        [correlationIdHeader]: correlationId,
                    },
                },
                body: JSON.stringify({ query, variables }),
            });
        } catch (e) {
            throw new ClientGraphqlNetworkError(
                e instanceof Error ? e : new Error(String(e)),
            );
        }

        const setGraphqlErrorContext = () => {
            setErrorTrackingContext({
                query,
                correlationId,
            });
        };

        if (!res.ok) {
            setGraphqlErrorContext();
            await throwGraphqlError(res);
        }

        const json = await res.json();

        if (json.errors) {
            setGraphqlErrorContext();
            throw new ClientGraphqlError(correlationId, json.errors);
        }

        return json.data;
    };
}
